/**
 * §B10's umpire popover — the what-if picker, and only ever on the AI half.
 *
 * It is deliberately the technical page's `../appointments/OfficialPicker.tsx` with one rule
 * changed and one removed:
 *
 *  * **Every blocked option is `disabled`.** §B10: *"Blocked options are `disabled` with a red
 *    flag chip carrying one of exactly three strings"* — and the fourth the Avoid VU field
 *    adds. There is no override anywhere in module 02: §B4 says in as many words that the TD
 *    cannot correct an umpire appointment here at all, so the technical page's struck-through,
 *    still-choosable `Own Country` would be offering a second step that does not exist.
 *  * **There is no ATD list.** Nobody from the technical table stands on the pitch.
 *
 * The chip's words come from the shared engine and are never composed here, so a name the
 * draft will not propose and a name this list greys out carry the same sentence.
 */

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import type { UmpireSlot } from "@hockeytd/shared";

import type { OfficialView, UmpireFixtureBody } from "../../api/types.js";
import { initialsOf, umpirePickerOptions } from "./vocabulary.js";

/** The popover's own width, so it can be kept on screen. Matches the stylesheet. */
const WIDTH = 326;
const MAX_HEIGHT = 380;

export interface UmpirePickerProps {
  fixture: UmpireFixtureBody;
  slot: UmpireSlot;
  /** The whole roster; the list filters itself to umpires. */
  officials: readonly OfficialView[];
  /** Every fixture on screen — the on-field-per-day count crosses both competitions (§B5). */
  allFixtures: readonly UmpireFixtureBody[];
  anchor: HTMLElement | null;
  onPick: (tmsPersonId: string) => void;
  onClose: () => void;
}

export function UmpirePicker({
  fixture,
  slot,
  officials,
  allFixtures,
  anchor,
  onPick,
  onClose,
}: UmpirePickerProps): React.JSX.Element {
  const boxRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const [search, setSearch] = useState("");
  const [position, setPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  const all = useMemo(
    () => umpirePickerOptions(fixture, slot, officials, allFixtures),
    [fixture, slot, officials, allFixtures],
  );

  const shown = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (needle === "") return all;
    return all.filter(
      (option) =>
        option.official.mediaName.toLowerCase().includes(needle) ||
        option.official.country.toLowerCase().includes(needle),
    );
  }, [all, search]);

  useLayoutEffect(() => {
    if (anchor === null) return;
    function place(): void {
      if (anchor === null) return;
      const box = anchor.getBoundingClientRect();
      const top = Math.max(12, Math.min(box.bottom + 6, window.innerHeight - MAX_HEIGHT));
      const left = Math.max(12, Math.min(box.left, window.innerWidth - WIDTH - 12));
      setPosition({ top, left });
    }
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [anchor]);

  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  useEffect(() => {
    function onDocumentPointerDown(event: MouseEvent): void {
      const box = boxRef.current;
      if (box === null) return;
      const target = event.target;
      if (target instanceof Node && (box.contains(target) || anchor?.contains(target) === true)) {
        return;
      }
      onClose();
    }
    function onDocumentKeyDown(event: KeyboardEvent): void {
      if (event.key === "Escape") onClose();
    }
    document.addEventListener("mousedown", onDocumentPointerDown);
    document.addEventListener("keydown", onDocumentKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocumentPointerDown);
      document.removeEventListener("keydown", onDocumentKeyDown);
    };
  }, [anchor, onClose]);

  /** Arrow keys walk the choosable options; Home and End jump to the ends. */
  function onKeyDown(event: React.KeyboardEvent<HTMLDivElement>): void {
    if (event.key === "Escape") {
      event.preventDefault();
      onClose();
      return;
    }
    const keys = ["ArrowDown", "ArrowUp", "Home", "End"];
    if (!keys.includes(event.key)) return;
    const box = boxRef.current;
    if (box === null) return;
    const buttons = [...box.querySelectorAll<HTMLButtonElement>("button[data-option]")].filter(
      (button) => !button.disabled,
    );
    if (buttons.length === 0) return;
    event.preventDefault();
    const active = document.activeElement;
    const at = buttons.findIndex((button) => button === active);
    const next =
      event.key === "Home"
        ? 0
        : event.key === "End"
          ? buttons.length - 1
          : event.key === "ArrowDown"
            ? at + 1 >= buttons.length
              ? 0
              : at + 1
            : at <= 0
              ? buttons.length - 1
              : at - 1;
    buttons[next]?.focus();
  }

  const teams = `${fixture.homeTeamCode ?? "TBC"} v ${fixture.awayTeamCode ?? "TBC"}`;

  return (
    <div
      className="dropdown show"
      role="dialog"
      aria-label={`Choose an umpire for the ${fixture.displayMatchNo} ${slot} slot`}
      ref={boxRef}
      style={{ top: position.top, left: position.left }}
      onKeyDown={onKeyDown}
      data-testid={`umpire-picker-${String(fixture.tmsMatchDbId)}-${slot}`}
    >
      <div className="dropdown__head">
        <span className="dropdown__role">{slot}</span>
        {/* §B10: the mark is always ✅ — nothing here is a breach the TD could be asked to fix. */}
        <span className="dropdown__mark" aria-hidden="true">
          ✓
        </span>
        <span className="dropdown__ctx">
          {fixture.displayMatchNo} · {teams}
        </span>
      </div>

      <div className="dropdown__search">
        <input
          ref={searchRef}
          type="search"
          aria-label="Search umpires by name or country"
          placeholder="Search by name or country"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
          }}
        />
      </div>

      {shown.length === 0 ? (
        <p className="dropdown__none">
          No umpire on the roster matches that, and can take the {slot} slot.
        </p>
      ) : null}

      <ul className="dropdown__list">
        {shown.map((option) => {
          const { official, reason } = option;
          return (
            <li key={official.tmsPersonId}>
              <button
                type="button"
                data-option={official.tmsPersonId}
                data-testid={`umpire-option-${official.tmsPersonId}`}
                data-blocked={reason ?? undefined}
                className={official.gender === null ? undefined : `g-${official.gender}`}
                disabled={reason !== null}
                title={reason ?? undefined}
                onClick={() => {
                  onPick(official.tmsPersonId);
                }}
              >
                <span
                  className={`oavatar${official.gender === null ? "" : ` oavatar--${official.gender}`}`}
                  aria-hidden="true"
                >
                  {initialsOf(official.mediaName)}
                </span>
                <span className="o-nm">{official.mediaName}</span>
                {reason === null ? (
                  <span className="o-nat num">{official.country}</span>
                ) : (
                  <span className="o-flag o-flag--umpire-blocked">{reason}</span>
                )}
              </button>
            </li>
          );
        })}
      </ul>

      <div className="dropdown__foot">
        Umpires from either competing country are blocked, as is anyone already in this match
        or already on-field that day. So is an umpire who does not work this competition, one
        who has already umpired the limit of days running, and one who cannot be paired with
        the umpire in the other on-field slot.
        <span className="photonote">
          Photos come from the officials profiles; initials stand in until they arrive.
        </span>
      </div>
    </div>
  );
}
